/**
 * OnboardingBanner — soft warning strip rendered on Mission Control
 * surfaces when the current org has an incomplete onboarding session.
 * Renders nothing when the org has no session OR the session is
 * complete, so it's safe to mount globally.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../../lib/authedFetch';
import { Sparkles, X, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'seerios_onboarding_banner_dismissed';

export default function OnboardingBanner({ orgId }) {
  const [summary, setSummary] = useState(null);
  const [showDetails, setShowDetails] = useState(() => {
    try { return localStorage.getItem('mc.showDetails') === '1'; } catch { return false; }
  });
  const [dismissedForOrgs, setDismissedForOrgs] = useState(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]'));
    } catch {
      return new Set();
    }
  });
  const navigate = useNavigate();

  // The banner is calm-by-default everywhere; it only surfaces when
  // the user has opted into "Show details" on Mission Control.
  useEffect(() => {
    const handler = (e) => setShowDetails(!!e.detail);
    window.addEventListener('mc:show-details', handler);
    return () => window.removeEventListener('mc:show-details', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!orgId) return undefined;
    (async () => {
      try {
        const r = await authedFetch(`${API}/api/onboarding/banner/${orgId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setSummary(d);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  const dismiss = () => {
    setDismissedForOrgs((prev) => {
      const next = new Set(prev);
      next.add(orgId);
      try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  };

  if (!showDetails) return null;
  if (!summary) return null;
  if (summary.status === 'complete' || summary.status === 'not_started') return null;
  if (summary.completion_pct === 100) return null;
  if (dismissedForOrgs.has(orgId)) return null;

  const missingCount = (summary.missing || []).length;

  return (
    <div
      className="mx-4 md:mx-8 lg:mx-12 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center justify-between gap-3"
      data-testid="onboarding-banner"
      data-completion={summary.completion_pct}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="h-4 w-4 text-amber-600 shrink-0" />
        <div className="min-w-0">
          <span className="text-sm font-semibold text-amber-900">
            Incomplete onboarding — {missingCount} item{missingCount === 1 ? '' : 's'} missing.
          </span>{' '}
          <span className="text-xs text-amber-800">
            {summary.completion_pct}% done · next: <strong>{summary.next_step || '—'}</strong>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => navigate(`/admin/onboarding/${orgId}`)}
          className="text-xs font-semibold text-amber-900 hover:underline inline-flex items-center"
          data-testid="onboarding-banner-resume"
        >
          Resume <ChevronRight className="h-3 w-3 ml-0.5" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="h-6 w-6 inline-flex items-center justify-center rounded text-amber-700 hover:bg-amber-100"
          data-testid="onboarding-banner-dismiss"
          aria-label="Dismiss for this session"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
